"use client";

import { useMemo, useState } from "react";

import { SectionBody, SectionCard } from "@/features/candidate/components/profile/section-layout";
import { useGetTechnicalProfile } from "@/lib/api/generated";
import type { ProfileDetailSchema } from "@/lib/api/model";

import { COLLAPSED_REPO_COUNT } from "../../lib/constants";
import { extractStackoverflowId } from "../../lib/extract-stackoverflow-id";

import { GithubBlock } from "./github-block";
import { TechnicalProfilePending } from "./pending";
import { ShowMoreButton } from "./show-more-button";
import { StackOverflowBlock } from "./stackoverflow-block";

export interface TechnicalProfileSectionProps {
  profile: ProfileDetailSchema;
}

/**
 * Renders the "Technical Profile" card below the Experience section of the
 * talent search profile sheet whenever the candidate has a GitHub or
 * Stack Overflow link. Data comes from the dedicated backend route
 * (`GET /api/profiles/{id}/technical-profile`) which proxies the public
 * APIs through Bright Data — so this card never blocks the main profile
 * load and skeleton-paints in the background.
 */
export function TechnicalProfileSection({
  profile,
}: TechnicalProfileSectionProps) {
  const [expanded, setExpanded] = useState(false);

  const githubUsername = profile.github_username?.trim() || null;
  const stackoverflowUserId = useMemo(
    () => extractStackoverflowId(profile.social_profiles),
    [profile.social_profiles],
  );

  const hasTechnicalSocial = Boolean(githubUsername) || stackoverflowUserId !== null;

  const { data, isPending } = useGetTechnicalProfile(profile.profile_id, {
    query: {
      enabled: hasTechnicalSocial,
      staleTime: 60 * 60 * 1000,
      retry: false,
    },
  });

  if (!hasTechnicalSocial) return null;

  const githubProfile = data?.github ?? null;
  const stackoverflowProfile = data?.stackoverflow ?? null;

  const hasResolvedAnyBlock =
    githubProfile !== null || stackoverflowProfile !== null;

  if (!isPending && !hasResolvedAnyBlock) {
    return null;
  }

  const showGithubSkeleton = Boolean(githubUsername);
  const showStackOverflowSkeleton = stackoverflowUserId !== null;

  const showShowMore =
    !isPending &&
    githubProfile !== null &&
    (githubProfile.user.public_repos ?? 0) > COLLAPSED_REPO_COUNT;

  return (
    <SectionCard title="Technical Profile">
      <SectionBody className="gap-5">
        {isPending ? (
          <TechnicalProfilePending
            showGithub={showGithubSkeleton}
            showStackOverflow={showStackOverflowSkeleton}
          />
        ) : (
          <>
            {stackoverflowProfile && (
              <StackOverflowBlock profile={stackoverflowProfile} />
            )}
            {stackoverflowProfile && githubProfile && (
              <div className="h-px w-full bg-border" />
            )}
            {githubProfile && (
              <GithubBlock profile={githubProfile} expanded={expanded} />
            )}
          </>
        )}

        {showShowMore && (
          <ShowMoreButton
            expanded={expanded}
            onToggle={() => setExpanded((v) => !v)}
          />
        )}
      </SectionBody>
    </SectionCard>
  );
}
